import Entity from 'src/app/core/domain/entity';
import UniqueEntityID from 'src/app/core/domain/unique_entity_id';
import { PatientSerie } from 'src/app/data/patient/domain/patient_serie/patient_serie';

import { FieldType } from '../../../../ui/template/routes/editor/components/fields-library/field_type';
import { TemplateField, TemplateFieldId } from '../templates/template_field';
import { NoteId } from './note';

export interface NoteFieldProps {
  noteId: NoteId;
  notesTemplateFieldId: TemplateFieldId;
  label: string;
  updatedAt?: Date;
  type: FieldType;
  params: unknown;
  value: unknown;
  serieItemId?: string;
  order: number;
}

export class NoteFieldId extends Entity<unknown> {
  private constructor(id?: UniqueEntityID) {
    super(null, id);
  }

  public static create(id?: UniqueEntityID): NoteFieldId {
    return new NoteFieldId(id);
  }
}

export class NoteField extends Entity<NoteFieldProps> {
  get noteFieldId(): NoteFieldId {
    return NoteFieldId.create(this.id);
  }

  get notesTemplateFieldId(): TemplateFieldId {
    return this.props.notesTemplateFieldId;
  }

  get label(): string {
    return this.props.label;
  }

  get updatedAt(): Date | undefined {
    return this.props.updatedAt;
  }

  get type(): FieldType {
    return this.props.type;
  }

  get params(): unknown {
    return this.props.params;
  }

  get value(): unknown {
    return this.props.value;
  }

  get serieItemId(): string | undefined {
    return this.props.serieItemId;
  }

  get order(): number {
    return this.props.order;
  }

  get matchingPatientSerie(): PatientSerie | undefined {
    return NoteField.patientSerieMatches[this.props.type];
  }

  private constructor(props: NoteFieldProps, id?: UniqueEntityID) {
    super(props, id);
  }

  copyWith(props: NoteFieldProps): NoteField {
    return NoteField.create(
      {
        ...this.props,
        ...props,
      },
      this.id,
    );
  }

  patch(notesTemplateField: TemplateField): NoteField {
    return this.copyWith({
      notesTemplateFieldId: notesTemplateField.templateFieldId,
      label: notesTemplateField.label,
      type: notesTemplateField.type,
      params: notesTemplateField.params,
      order: notesTemplateField.order,
      value: this.convertValue(this.value),
    } as NoteFieldProps);
  }

  private convertValue(value: unknown): unknown {
    switch (this.type) {
      case FieldType.Information:
        return null;
      case FieldType.MultiLineText:
      case FieldType.Text:
        return value as string;
      case FieldType.Checkbox:
        return typeof value === 'boolean'
          ? value
          : typeof value === 'string'
          ? value == 'true'
          : false;
      case FieldType.Date:
      case FieldType.DateOfBirth:
        return value instanceof Date
          ? value
          : typeof value === 'string'
          ? new Date(value as string)
          : undefined;
      case FieldType.Today:
        return value instanceof Date
          ? value
          : typeof value === 'string'
          ? new Date(value as string)
          : new Date();
      case FieldType.Number:
      case FieldType.ArmSize:
      case FieldType.FatMass:
      case FieldType.FormWeight:
      case FieldType.HipSize:
      case FieldType.MuscularMass:
      case FieldType.Size:
      case FieldType.ThighSize:
      case FieldType.WaistSize:
      case FieldType.Weight:
        return Number(value);
      case FieldType.Range:
      case FieldType.Assessment:
      case FieldType.InvAssessment:
        return Number(value ?? 0);
      case FieldType.NzAssessment:
        return Number(value ?? 1);

      default:
        return value;
    }
  }

  updateValue(value: unknown): NoteField {
    return this.copyWith({
      value: this.convertValue(value),
    } as NoteFieldProps);
  }

  updateSerieItemId(serieItemId: string): NoteField {
    return this.copyWith({
      serieItemId,
    } as NoteFieldProps);
  }

  public static create(props: NoteFieldProps, id?: UniqueEntityID): NoteField {
    return new NoteField(props, id);
  }

  private static patientSerieMatches: { [key: string]: PatientSerie } = {
    [FieldType.ArmSize]: PatientSerie.ArmSize,
    [FieldType.FatMass]: PatientSerie.FatMass,
    [FieldType.FormWeight]: PatientSerie.FormWeight,
    [FieldType.HipSize]: PatientSerie.HipSize,
    [FieldType.MuscularMass]: PatientSerie.MuscularMass,
    [FieldType.Size]: PatientSerie.Size,
    [FieldType.ThighSize]: PatientSerie.ThighSize,
    [FieldType.WaistSize]: PatientSerie.WaistSize,
    [FieldType.Weight]: PatientSerie.Weight,
  };
}
